import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  Component,
  OnInit,
  PLATFORM_ID,
  Inject,
  Input,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';
import { ChevronDownIcon, LucideAngularModule, UserIcon } from 'lucide-angular';
import { DialogService, DynamicDialogModule } from 'primeng/dynamicdialog';
import { MainLoginComponent } from '../main-login/main-login.component';
import { EmailValidationService } from '../../../services/email-validation.service';
import { Subscription } from 'rxjs';
import { User } from '../../../types/auth.types';
import { PopoverModule } from 'primeng/popover';

interface MenuItem {
  icon: string;
  title: string;
  description: string;
  action: () => void;
}

@Component({
  selector: 'allas-user-menu',
  imports: [
    CommonModule,
    LucideAngularModule,
    DynamicDialogModule,
    PopoverModule,
  ],
  templateUrl: 'user-menu.html',
  providers: [DialogService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserMenuComponent implements OnInit, OnDestroy {
  readonly userIcon = UserIcon;
  readonly chevronDownIcon = ChevronDownIcon;

  isOpen = false;
  isDesktop = true;
  currentUser: User | null = null;
  showCurrentUser = false;

  @Input() isInStickyHeader = false;

  private authSubscription = new Subscription();
  private readonly desktopBreakpoint = 1024;

  constructor(
    private router: Router,
    private dialogService: DialogService,
    private emailValidationService: EmailValidationService,
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  ngOnInit(): void {
    this.initializeComponent();
  }

  ngOnDestroy(): void {
    this.cleanupSubscriptions();
    this.removeStorageListener();
  }

  toggleMenu(): void {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.checkAuthenticationStatus();
    }
  }

  closeMenu(): void {
    this.isOpen = false;
  }

  onMenuItemClick(item: MenuItem): void {
    item.action();
    this.closeMenu();
  }

  onLoginClick(): void {
    this.navigateToLogin();
    this.closeMenu();
  }

  logout(): void {
    this.performLogout();
    this.closeMenu();
    this.reloadPage();
  }

  getUserDisplayName(): string {
    return this.currentUser?.nombrePreferido || 'Usuario';
  }

  get menuItems(): MenuItem[] {
    return this.getGuestMenuItems();
  }

  private initializeComponent(): void {
    this.verifyIsDesktop();
    this.checkAuthenticationStatus();
    this.setupStorageListener();
  }

  private verifyIsDesktop(): void {
    if (this.isBrowser()) {
      this.isDesktop = window.innerWidth > this.desktopBreakpoint;
    }
  }

  private setupStorageListener(): void {
    if (this.isBrowser()) {
      window.addEventListener('storage', this.handleStorageChange.bind(this));
    }
  }

  private removeStorageListener(): void {
    if (this.isBrowser()) {
      window.removeEventListener(
        'storage',
        this.handleStorageChange.bind(this)
      );
    }
  }

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  private getGuestMenuItems(): MenuItem[] {
    return [
      {
        icon: 'log-in',
        title: 'Encuentre un taller referido',
        description:
          'Utilice nuestro programa de recomendación de taller de reparaciones',
        action: () => this.handleLoginAction(),
      },
      {
        icon: 'gift',
        title: 'Gane recompensas',
        description: 'Conozca nuestro programa de recompensas',
        action: () => this.handleRewardsAction(),
      },
      {
        icon: 'package',
        title: 'Seguimiento de pedidos',
        description:
          'Utilice el correo electrónico y el número de pedido para rastrear un pedido',
        action: () => this.handleTrackOrderAction(),
      },
    ];
  }

  private handleLoginAction(): void {
    if (this.isDesktop) {
      this.openLoginModal();
    } else {
      this.navigateToLogin();
    }
  }

  private openLoginModal(): void {
    const modalData = { showHeader: false };
    const modalConfig = {
      modal: true,
      draggable: false,
      resizable: false,
      closable: true,
      data: modalData,
    };

    this.dialogService.open(MainLoginComponent, modalConfig);
  }

  private navigateToLogin(): void {
    this.router.navigate(['/login']);
  }

  private handleRewardsAction(): void {
    // Placeholder for rewards functionality
  }

  private handleTrackOrderAction(): void {
    // Placeholder for track order functionality
  }

  private checkAuthenticationStatus(): void {
    if (!this.isBrowser()) {
      return;
    }

    const previousUser = this.currentUser;
    this.currentUser = this.getCurrentUserFromService();

    if (!this.hasValidToken()) {
      this.currentUser = null;
    }

    this.handleUserStateChange(previousUser);

    this.setShowCurrentUser(Boolean(this.currentUser));
  }

  private setShowCurrentUser(show: boolean): void {
    setTimeout(() => {
      this.showCurrentUser = show;
      this.cdr.detectChanges();
    }, 10);
  }

  private getCurrentUserFromService(): User | null {
    return this.emailValidationService.getCurrentUser();
  }

  private hasValidToken(): boolean {
    if (!this.isBrowser()) {
      return false;
    }
    const token = localStorage.getItem('authToken');
    return !!token;
  }

  private handleUserStateChange(previousUser: User | null): void {
    if (this.userLoggedOut(previousUser) && this.isMenuOpen()) {
      this.closeMenu();
    }
  }

  private userLoggedOut(previousUser: User | null): boolean {
    return !!previousUser && !this.currentUser;
  }

  private isMenuOpen(): boolean {
    return this.isOpen;
  }

  private performLogout(): void {
    this.emailValidationService.logout();
    this.currentUser = null;
    this.setShowCurrentUser(false);
  }

  private reloadPage(): void {
    if (this.isBrowser()) {
      window.location.reload();
    }
  }

  private cleanupSubscriptions(): void {
    if (this.authSubscription) {
      this.authSubscription.unsubscribe();
    }
  }

  private handleStorageChange(event: StorageEvent): void {
    if (this.isRelevantStorageChange(event)) {
      this.checkAuthenticationStatus();
    }
  }

  private isRelevantStorageChange(event: StorageEvent): boolean {
    return event.key === 'userData' || event.key === 'authToken';
  }
}
